import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }
}

// SETUP COLORS

const GREY = {
  0: '#F4F6FF',
  100: '#E6EBF5',
  200: '#D7DEEB',
  300: '#B9C5D8',
  400: '#8FA3BC',
  500: '#6A809F',
  600: '#4A6383',
  700: '#324967',
  800: '#1C324B',
  900: '#052033',
};

const PRIMARY = {
  lighter: '#D1E9FF',
  light: '#76BBFF',
  main: '#1F92FE',
  dark: '#0063CB',
  darker: '#004795',
  contrastText: '#F4F6FF',
};

const SECONDARY = {
  lighter: '#D7DEEB',
  light: '#8FA3BC',
  main: '#6A809F',
  dark: '#324967',
  darker: '#1C324B',
  contrastText: '#FFFFFF',
};

const INFO = {
  lighter: '#D8F0F5',
  light: '#9DDBEA',
  main: '#6BC6D9',
  dark: '#4590A0',
  darker: '#2A5F6A',
  contrastText: '#FFFFFF',
};

const SUCCESS = {
  lighter: '#CCFAE1',
  light: '#67E9AD',
  main: '#00CD82',
  dark: '#00965F',
  darker: '#00633F',
  contrastText: '#ffffff',
};

const WARNING = {
  lighter: '#FFF8D6',
  light: '#FFEB7A',
  main: '#FEDB07',
  dark: '#D1B300',
  darker: '#8C7700',
  contrastText: GREY[800],
};

const ERROR = {
  lighter: '#FFD6D7',
  light: '#FF9498',
  main: '#FF464B',
  dark: '#D11D22',
  darker: '#8C0F13',
  contrastText: '#FFFFFF',
};

const COMMON = {
  common: {
    black: '#052033',
    white: '#F4F6FF',
  },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  divider: alpha(GREY[500], 0.2),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export function palette(mode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[800],
      secondary: GREY[600],
      disabled: GREY[500],
    },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      neutral: GREY[200],
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  };

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#FFFFFF',
      secondary: GREY[500],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[800],
      default: GREY[900],
      neutral: alpha(GREY[500], 0.12),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  };

  return mode === 'light' ? light : dark;
}
